import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Client, CLIENT_RISK_STATUS_LABELS } from '@/types/client';
import { clientMasterApi, clientApprovalAPI } from '@/services/api';
import { useScope } from '@/hooks/useScope';
import { CLIENT_MASTER_EXPORT, READ_CLIENT_MASTER, WRITE_CLIENT_MASTER } from '@/constants/scopeCodes';
import { Tooltip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import debounce from 'lodash.debounce';
import { useClientSupportingData } from './hooks/useClientSupportingData';
import shared from '@/components/utils/shared.module.css';
import { PageFrame, PageHeader, Surface, FilterBar, FilterSelect, Button as UiButton, Pill, RowActionMenu, confirmColors } from '@/components/ui';
import { describeRequestError } from '@/utils/apiErrors';
import { formatClientName } from '@/utils/formatters';
import {
  REASON_LABEL_STYLE,
  REASON_REQUIRED_MARK,
  REASON_SELECT_STYLE,
  REASON_TEXTAREA_STYLE,
} from './utils/changeReasonStyles';
import type { FilterOption } from '@/components/ui';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';

interface ClientListProps {
  onClientSelect?: (client: Client) => void;
  onModified?: () => void;
}

const CLIENT_TYPE_OPTIONS: FilterOption[] = [
  { value: 'PMS', label: 'PMS' },
  { value: 'AIF', label: 'AIF' },
];
const YES_NO_OPTIONS: FilterOption[] = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];
const STATUS_OPTIONS: FilterOption[] = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

const parseBoolFilter = (value: string): boolean | undefined =>
  value !== '' ? value === 'true' : undefined;


const ClientList: React.FC<ClientListProps> = ({ onClientSelect, onModified }) => {
  const navigate = useNavigate();
  const canWrite = useScope(WRITE_CLIENT_MASTER);
  // Either scope: syncing republishes what the page already shows.
  const canSyncToZoho = useScope([READ_CLIENT_MASTER, WRITE_CLIENT_MASTER]);
  // Export is a separate grant — reading the list does not entitle a user to
  // download the whole book of KYC, banking and fee data.
  const canExport = useScope(CLIENT_MASTER_EXPORT);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination & Search State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Filter States
  const [filterType, setFilterType] = useState<string>('');
  const [filterGroup, setFilterGroup] = useState<string>('');
  const [filterClass, setFilterClass] = useState<string>('');
  const [filterScheme, setFilterScheme] = useState<string>('');
  const [filterStopPurchase, setFilterStopPurchase] = useState<string>('');
  const [filterRedemptionOnly, setFilterRedemptionOnly] = useState<string>('');
  const [filterIsActive, setFilterIsActive] = useState<string>('');
  const [exporting, setExporting] = useState(false);
  const [syncingToZoho, setSyncingToZoho] = useState(false);

  const { groups, clientClasses, schemes } = useClientSupportingData(filterType as any || 'PMS');

  const groupOptions: FilterOption[] = useMemo(
    () => groups.map(g => ({ value: g.id.toString(), label: g.group_name || g.name })),
    [groups],
  );
  const classOptions: FilterOption[] = useMemo(
    () => clientClasses.map(c => ({ value: c.id.toString(), label: c.name })),
    [clientClasses],
  );
  const schemeOptions: FilterOption[] = useMemo(
    () => (schemes ?? []).map(s => ({ value: s.id.toString(), label: s.code ? `${s.code} - ${s.name}` : s.name })),
    [schemes],
  );
  const asSelected = (opts: FilterOption[], val: string): FilterOption | null =>
    opts.find(o => o.value === val) ?? null;

  const [pendingCount, setPendingCount] = useState(0);
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);


  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await clientMasterApi.getClients({
        page: page + 1, // API is 1-indexed
        page_size: rowsPerPage,
        search: debouncedSearchTerm,
        ordering: '-is_active',
        client_type: (filterType as any) || undefined,
        group: filterGroup ? parseInt(filterGroup) : undefined,
        client_class: filterClass ? parseInt(filterClass) : undefined,
        scheme: filterScheme ? parseInt(filterScheme) : undefined,
        stop_purchase: parseBoolFilter(filterStopPurchase),
        redemption_only: parseBoolFilter(filterRedemptionOnly),
        is_active: parseBoolFilter(filterIsActive),
      });
      setClients(response.results);
      setTotalCount(response.count);
      setError(null);
    } catch (err) {
      setError('Failed to fetch clients');
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearchTerm, filterType, filterGroup, filterClass, filterScheme, filterStopPurchase, filterRedemptionOnly, filterIsActive]);

  // One list, so a new filter is added in a single place. The badge does not
  // count the search term (the shared bar's rule), but the export does narrow
  // by it — so the export tooltip has to.
  const activeFilters = [filterType, filterGroup, filterClass, filterScheme, filterStopPurchase, filterRedemptionOnly, filterIsActive];
  const activeFilterCount = activeFilters.filter(Boolean).length;
  const exportIsFiltered = activeFilterCount > 0 || !!debouncedSearchTerm;

  const fetchPendingCount = useCallback(async () => {
    try {
      const response = await clientApprovalAPI.getPendingCount();
      setPendingCount(response.count);
    } catch (err) {
      console.error('Error fetching pending count:', err);
    }
  }, []);

  useEffect(() => {
    fetchClients();
    fetchPendingCount();
  }, [fetchClients, fetchPendingCount]);

  // Debounced search updater
  const updateSearchQuery = useMemo(
    () => debounce((value: string) => {
      setDebouncedSearchTerm(value);
      setPage(0); // Reset to first page on new search
    }, 500),
    []
  );

  const handleViewDetails = useCallback((client: Client) => {
    if (onClientSelect) {
      onClientSelect(client);
    } else {
      const path = `/client-master/${client.id}`;
      navigate(path);
    }
  }, [navigate, onClientSelect]);

  const handleEdit = useCallback((client: Client) => {
    if (onClientSelect) {
      onClientSelect(client);
    } else {
      const path = `/client-master/${client.id}/edit`;
      navigate(path);
    }
  }, [navigate, onClientSelect]);

  const handleActivateDeactivate = useCallback(async (client: Client) => {
    const action = client.is_active ? 'deactivate' : 'activate';
    const result = await Swal.fire({
      title: 'Reason for Change',
      html: `<div style="text-align:left;overflow:hidden;">
        <p style="margin:0 0 12px;">Please provide a reason to ${action} client <b>${client.full_name}</b>.</p>
        <label style="${REASON_LABEL_STYLE}">Reason ${REASON_REQUIRED_MARK}</label>
        <select id="swal-reason-dropdown" style="${REASON_SELECT_STYLE}">
          <option value="">-- Select a reason --</option>
          <option value="Full Redemption">Full Redemption</option>
          <option value="Partial Redemption">Partial Redemption</option>
          <optgroup label="Transfer">
            <option value="Death">Death</option>
            <option value="Gift">Gift</option>
            <option value="Stock Transfer">Stock Transfer</option>
          </optgroup>
          <option value="Other">Other</option>
        </select>
        <label style="${REASON_LABEL_STYLE}">Additional Notes</label>
        <textarea id="swal-reason-notes" rows="3" placeholder="Enter additional notes..." style="${REASON_TEXTAREA_STYLE}"></textarea>
      </div>`,
      icon: 'warning',
      showCancelButton: true,
      ...confirmColors('primary'),
      confirmButtonText: `Submit for Approval`,
      preConfirm: () => {
        const selectedReason = (document.getElementById('swal-reason-dropdown') as HTMLSelectElement)?.value || '';
        const notes = (document.getElementById('swal-reason-notes') as HTMLTextAreaElement)?.value?.trim() || '';

        if (!selectedReason) {
          Swal.showValidationMessage('Please select a reason from the dropdown');
          return false;
        }
        if (selectedReason === 'Other' && !notes) {
          Swal.showValidationMessage('Please provide details when selecting "Other"');
          return false;
        }

        if (selectedReason && notes) {
          return `${selectedReason} - ${notes}`;
        }
        return selectedReason;
      },
    });

    if (!result.isConfirmed || !result.value) return;

    try {
      setActionLoadingId(client.id as number);
      const payload = { change_reason: result.value.trim() };
      if (client.is_active) {
        await clientMasterApi.deactivateClient(client.id as number, payload);
      } else {
        await clientMasterApi.activateClient(client.id as number, payload);
      }

      if (onModified) onModified();
      await fetchClients();

      Swal.fire(
        'Submitted!',
        `${action.charAt(0).toUpperCase() + action.slice(1)} request has been submitted for approval.`,
        'success'
      );
    } catch (err: any) {
      console.error(`Failed to ${action} client`, err);
      const errorMsg = err?.response?.data?.error || `Failed to ${action} client.`;
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setActionLoadingId(null);
    }
  }, [fetchClients, onModified]);

  const handleNavigateToApprovals = useCallback(() => {
    navigate('/client-approvals');
  }, [navigate]);

  const handleNavigateToNewClient = useCallback(() => {
    navigate('/client-master/new');
  }, [navigate]);

  // Pushes only clients whose data changed since their last successful sync; the filters
  // above deliberately do not narrow it, since staleness is not a visible column.
  const handleSyncAllToZoho = useCallback(async () => {
    try {
      setSyncingToZoho(true);
      const totals = await clientMasterApi.syncAllClientsToZoho();
      const awaiting = totals.awaiting_conversion > 0
        ? ` ${totals.awaiting_conversion} awaiting lead conversion: ` +
          `${totals.awaiting_conversion_fa_accounts.join(', ')}.`
        : '';
      if (totals.total === 0) {
        toast.info('Every client is already up to date in Zoho CRM.');
      } else if (totals.failed > 0) {
        const duplicated = totals.duplicated_fa_accounts.length > 0
          ? ` On more than one Zoho Contact: ${totals.duplicated_fa_accounts.join(', ')}.`
          : '';
        toast.warning(
          `Synced ${totals.synced} of ${totals.total} clients. ` +
          `Failed: ${totals.failed_fa_accounts.join(', ') || 'see server logs'}.` +
          duplicated + awaiting
        );
      } else if (totals.awaiting_conversion > 0) {
        // Not a failure: these clients sync on their own once the lead is converted.
        toast.info(`Synced ${totals.synced} of ${totals.total} clients.${awaiting}`);
      } else {
        toast.success(`Synced ${totals.synced} client(s) to Zoho CRM.`);
      }
    } catch (error) {
      toast.error(describeRequestError(error, 'sync to Zoho CRM'));
    } finally {
      setSyncingToZoho(false);
    }
  }, []);

  const handleExport = useCallback(async () => {
    try {
      setExporting(true);
      const blob = await clientMasterApi.exportClients({
        search: debouncedSearchTerm,
        client_type: (filterType as any) || undefined,
        group: filterGroup ? parseInt(filterGroup) : undefined,
        client_class: filterClass ? parseInt(filterClass) : undefined,
        scheme: filterScheme ? parseInt(filterScheme) : undefined,
        stop_purchase: parseBoolFilter(filterStopPurchase),
        redemption_only: parseBoolFilter(filterRedemptionOnly),
        is_active: parseBoolFilter(filterIsActive),
        format: 'xlsx'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Client_Master_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Failed to export clients', err);
      toast.error('Failed to export clients');
    } finally {
      setExporting(false);
    }
  }, [debouncedSearchTerm, filterType, filterGroup, filterClass, filterScheme, filterStopPurchase, filterRedemptionOnly, filterIsActive]);

  /* Columns replace the bespoke <ClientRow>: same fields, but the head, the
     sort arrows, the empty state and the footer now come from DataTable like
     every other list in the app. */
  const columns = useMemo<ColumnDef<Client>[]>(() => [
    { key: 'client_code', header: 'Client Code', width: '130px', render: r => r.client_code },
    { key: 'name', header: 'Name', width: '240px',
      render: r => `${r.client_code} - ${formatClientName(r)}` },
    { key: 'pan_card', header: 'PAN', width: '140px', render: r => r.pan_card || '—' },
    { key: 'fa_account_no', header: 'Account No', width: '160px', render: r => r.fa_account_no || '—' },
    { key: 'scheme', header: 'Scheme', width: '170px',
      render: r => r.scheme_name || r.scheme?.name || '—' },
    { key: 'group', header: 'Group', width: '140px', render: r => r.group_name || '—' },
    { key: 'client_class', header: 'Class', width: '140px',
      render: r => r.client_class_name || '—' },
    { key: 'risk_status', header: 'Risk Status', width: '150px',
      render: r => (r.risk_status ? CLIENT_RISK_STATUS_LABELS[r.risk_status] : '—') },
    { key: 'is_nri', header: 'NRI', width: '90px',
      render: r => (r.is_nri ? 'Yes' : r.is_nri === false ? 'No' : '—') },
    { key: 'inception_date', header: 'Inception Date', width: '150px',
      render: r => new Date(r.inception_date).toLocaleDateString() },
    { key: 'status', header: 'Status', width: '160px',
      render: r =>
        r.has_pending_approval ? (
          <Pill tone="warning">Pending Approval</Pill>
        ) : (
          <Pill tone={r.is_active ? 'positive' : 'neutral'}>{r.is_active ? 'Active' : 'Inactive'}</Pill>
        ) },
    { key: 'actions', header: 'Action', width: '80px',
      render: r => {
        const isLoading = actionLoadingId === r.id;
        return (
          <RowActionMenu
            label={`Actions for ${r.client_code}`}
            actions={[
              {
                label: 'View Details',
                icon: <VisibilityIcon fontSize="small" />,
                disabled: isLoading,
                onClick: () => handleViewDetails(r),
              },
              {
                label: 'Edit',
                icon: <EditIcon fontSize="small" />,
                disabled: isLoading,
                hidden: !canWrite,
                onClick: () => handleEdit(r),
              },
              {
                label: r.is_active ? 'Deactivate' : 'Activate',
                icon: r.is_active ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />,
                tone: r.is_active ? 'danger' : 'positive',
                loading: isLoading,
                hidden: !canWrite,
                onClick: () => handleActivateDeactivate(r),
              },
            ]}
          />
        );
      } },
  ], [actionLoadingId, canWrite, handleViewDetails, handleEdit, handleActivateDeactivate]);

  // Loading state handling for initial load
  if (loading && clients.length === 0 && page === 0 && !searchTerm) {
    return <div className={shared.loading}>Loading clients...</div>;
  }

  if (error && clients.length === 0) {
    return <div className={shared['error-msg']}>Error: {error}</div>;
  }

  return (
    <PageFrame
      header={
        <PageHeader
          title="Clients"
          subtitle="Every PMS and AIF client, their scheme mapping and status."
          actions={
            <>
              <UiButton onClick={handleNavigateToApprovals}>
                Pending Approvals
                {pendingCount > 0 && <Pill tone="critical" plain>{pendingCount}</Pill>}
              </UiButton>
              {canExport && (
                <Tooltip
                  title={
                    exportIsFiltered
                      ? 'Export filtered clients as Excel'
                      : 'Export all clients as Excel'
                  }
                  arrow
                >
                  <span>
                    <UiButton onClick={handleExport} disabled={exporting}>
                      {exporting ? 'Exporting…' : 'Export'}
                    </UiButton>
                  </span>
                </Tooltip>
              )}
              {canSyncToZoho && (
                <Tooltip title="Push clients whose data changed to Zoho CRM" arrow>
                  <span>
                    <UiButton onClick={handleSyncAllToZoho} disabled={syncingToZoho}>
                      {syncingToZoho ? 'Syncing…' : 'Sync to Zoho'}
                    </UiButton>
                  </span>
                </Tooltip>
              )}
              {canWrite && (
                <UiButton variant="primary" onClick={handleNavigateToNewClient}>
                  Create Client
                </UiButton>
              )}
            </>
          }
        />
      }
    >
      <FilterBar
        searchValue={searchTerm}
        onSearchChange={value => { setSearchTerm(value); updateSearchQuery(value); setPage(0); }}
        searchPlaceholder="Search by client code, name, email, PAN or account number…"
        activeFilterCount={activeFilterCount}
        onClearFilters={() => {
          setFilterType('');
          setFilterGroup('');
          setFilterClass('');
          setFilterScheme('');
          setFilterStopPurchase('');
          setFilterRedemptionOnly('');
          setFilterIsActive('');
          setSearchTerm('');
          updateSearchQuery('');
          setPage(0);
        }}
        filters={
          <>
              <FilterSelect
                label="Client Type"
                value={asSelected(CLIENT_TYPE_OPTIONS, filterType)}
                onChange={opt => {
                  setFilterType(opt?.value ?? '');
                  // Only Class is cleared: it disappears for PMS, Group does not.
                  setFilterClass('');
                  setPage(0);
                }}
                options={CLIENT_TYPE_OPTIONS}
              />

              <FilterSelect
                label="Group"
                value={asSelected(groupOptions, filterGroup)}
                onChange={opt => { setFilterGroup(opt?.value ?? ''); setPage(0); }}
                options={groupOptions}
              />

              {(!filterType || filterType === 'AIF') && (
                <FilterSelect
                  label="Class"
                  value={asSelected(classOptions, filterClass)}
                  onChange={opt => { setFilterClass(opt?.value ?? ''); setPage(0); }}
                  options={classOptions}
                />
              )}

              <FilterSelect
                label="Scheme"
                value={asSelected(schemeOptions, filterScheme)}
                onChange={opt => { setFilterScheme(opt?.value ?? ''); setPage(0); }}
                options={schemeOptions}
              />

              <FilterSelect
                label="Stop Purchase"
                value={asSelected(YES_NO_OPTIONS, filterStopPurchase)}
                onChange={opt => { setFilterStopPurchase(opt?.value ?? ''); setPage(0); }}
                options={YES_NO_OPTIONS}
              />

              <FilterSelect
                label="Redemption Only"
                value={asSelected(YES_NO_OPTIONS, filterRedemptionOnly)}
                onChange={opt => { setFilterRedemptionOnly(opt?.value ?? ''); setPage(0); }}
                options={YES_NO_OPTIONS}
              />

              <FilterSelect
                label="Status"
                value={asSelected(STATUS_OPTIONS, filterIsActive)}
                onChange={opt => { setFilterIsActive(opt?.value ?? ''); setPage(0); }}
                options={STATUS_OPTIONS}
              />
          </>
        }
      />

      {/* Table */}
      <Surface>
        <DataTable<Client>
          embedded
          data={clients}
          columns={columns}
          rowKey={row => row.id}
          rowStyle={row =>
            row.has_pending_approval
              ? { backgroundColor: 'var(--warn-soft)' }
              : !row.is_active
                ? { opacity: 0.72 }
                : undefined
          }
          totalCount={totalCount}
          loading={loading}
          error={error}
          currentPage={page + 1}
          totalPages={Math.max(Math.ceil(totalCount / rowsPerPage), 1)}
          onPageChange={next => setPage(next - 1)}
          pageSize={rowsPerPage}
          pageSizeOptions={[10, 25, 50, 100]}
          onPageSizeChange={size => { setRowsPerPage(size); setPage(0); }}
          sortField=""
          sortDir="asc"
          onSort={() => undefined}
          hideSearch
          loadingMessage="Loading clients…"
          emptyMessage="No clients match these filters."
        />
      </Surface>

    </PageFrame>
  );
};

export default ClientList;
